/**
 * VARCreateForm Component
 *
 * Multi-step wizard for creating a new VAR (Value at Risk) after clicking on the map.
 * Step 1: Location validation (spatial lookup against GeoServer)
 * Step 2: Title & Details
 * Step 3: Classification (subcategory selection, category auto-derived)
 * Step 4: Review & Confirm → creates the VAR
 * Step 5: Photos (optional) — upload var_photo images using the new varId
 *
 * Issue: #290
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faSave,
  faMapMarkerAlt,
  faArrowRight,
  faArrowLeft,
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { CreateVARInput, VARCategory, VARSubcategory, VAR_SUBCATEGORY_MAP } from '../../../types/var';
import { getBackendUrl } from '../../../services/configService';
import { toDMM } from '../../../utils/coordinateFormatters';
import MultiFileUploader from '../../../components/upload/MultiFileUploader';
import './VARCreateForm.scss';

const SUBCATEGORY_GROUPS: { category: VARCategory; subcategories: VARSubcategory[] }[] = [
  {
    category: 'Infrastructure',
    subcategories: [
      'Community', 'Camp', 'Cottage/Cabin', 'Weather Station',
      'Communication Site', 'Industrial Site', 'Power Generation',
      'Airport/Airstrip', 'Mine', 'Bridge', 'Highway/Road',
      'Railway', 'Dam', 'Pipeline',
    ],
  },
  {
    category: 'Cultural Resource',
    subcategories: [
      'Cemetery/Burial Ground', 'Heritage Site', 'Archaeological Site',
      'Sacred/Spiritual Site', 'Culturally Modified Trees',
    ],
  },
  {
    category: 'Natural Resource',
    subcategories: [
      'Habitat Area', 'Watershed', 'Wetland',
      'Species at Risk Area', 'Research Site',
    ],
  },
];

interface LocationValidation {
  status: 'validating' | 'valid' | 'invalid' | 'error';
  regionName?: string;
  regionCode?: string;
  errorMessage?: string;
}

interface VARCreateFormProps {
  location: { lon: number; lat: number };
  onSubmit: (data: CreateVARInput) => Promise<string>; // returns var_id
  onDone: () => void;
  onCancel: () => void;
}

export const VARCreateForm: React.FC<VARCreateFormProps> = ({
  location,
  onSubmit,
  onDone,
  onCancel,
}) => {
  const [step, setStep] = useState(1);
  const totalSteps = 4;

  // Step 1: Location validation
  const [locationValidation, setLocationValidation] = useState<LocationValidation>({ status: 'validating' });

  // Step 2: Title & Details
  const [name, setName] = useState('');
  const [details, setDetails] = useState('');

  // Step 3: Classification
  const [subcategory, setSubcategory] = useState<VARSubcategory | ''>('');

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdVarId, setCreatedVarId] = useState<string | null>(null);

  // Photo uploader visibility (opens after VAR is created)
  const [showPhotoUploader, setShowPhotoUploader] = useState(false);

  // Derived category from subcategory
  const category = useMemo(() => {
    if (!subcategory) return null;
    return VAR_SUBCATEGORY_MAP[subcategory];
  }, [subcategory]);

  // Validate location on mount
  useEffect(() => {
    const validateLocation = async () => {
      setLocationValidation({ status: 'validating' });
      try {
        const backendUrl = getBackendUrl() || '';
        const url = `${backendUrl}/api/var/validate-location?lat=${location.lat}&lon=${location.lon}`;
        const response = await fetch(url, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          throw new Error(`Validation failed: ${response.statusText}`);
        }

        const result = await response.json();
        if (result.error === 'OUT_OF_BOUNDS') {
          setLocationValidation({
            status: 'invalid',
            errorMessage: 'This location is outside NWT fire management boundaries.',
          });
        } else if (result.region) {
          setLocationValidation({
            status: 'valid',
            regionName: result.region.name,
            regionCode: result.region.code,
          });
        } else {
          setLocationValidation({
            status: 'invalid',
            errorMessage: 'Could not determine region for this location.',
          });
        }
      } catch (err) {
        setLocationValidation({
          status: 'error',
          errorMessage: err instanceof Error ? err.message : 'Location validation failed',
        });
      }
    };

    validateLocation();
  }, [location]);

  // Step validation
  const canProceed = useMemo(() => {
    switch (step) {
      case 1: return locationValidation.status === 'valid';
      case 2: return name.trim().length > 0 && details.trim().length > 0;
      case 3: return subcategory !== '';
      case 4: return true;
      default: return false;
    }
  }, [step, locationValidation, name, details, subcategory]);

  const formatCoordinate = toDMM;

  const handleNext = useCallback(() => {
    if (step < totalSteps && canProceed) setStep(s => s + 1);
  }, [step, canProceed]);

  const handleBack = useCallback(() => {
    if (step > 1 && !createdVarId) setStep(s => s - 1);
  }, [step, createdVarId]);

  // Create VAR (step 4 submit)
  const handleCreateVAR = useCallback(async () => {
    if (submitting || !subcategory) return;
    setSubmitting(true);
    setError(null);

    try {
      const data: CreateVARInput = {
        name: name.trim(),
        details: details.trim(),
        var_subtype: subcategory,
        location: { lon: location.lon, lat: location.lat },
      };
      const varId = await onSubmit(data);
      setCreatedVarId(varId);
      setShowPhotoUploader(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create VAR');
    } finally {
      setSubmitting(false);
    }
  }, [name, details, subcategory, location, submitting, onSubmit]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (createdVarId) { onDone(); } else { onCancel(); }
    }
  }, [onCancel, onDone, createdVarId]);

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="wizard-step">
            <h4>Location Validation</h4>
            <div className="form-group location-display">
              <label><FontAwesomeIcon icon={faMapMarkerAlt} /> Coordinates</label>
              <div className="location-value">
                {formatCoordinate(location.lat, true)}, {formatCoordinate(location.lon, false)}
              </div>
            </div>
            <div className={`validation-result validation-${locationValidation.status}`}>
              {locationValidation.status === 'validating' && (
                <><FontAwesomeIcon icon={faSpinner} spin /> Validating location...</>
              )}
              {locationValidation.status === 'valid' && (
                <><FontAwesomeIcon icon={faCheckCircle} /> Region: {locationValidation.regionName} ({locationValidation.regionCode})</>
              )}
              {(locationValidation.status === 'invalid' || locationValidation.status === 'error') && (
                <><FontAwesomeIcon icon={faExclamationTriangle} /> {locationValidation.errorMessage}</>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="wizard-step">
            <h4>Title &amp; Details</h4>
            <div className="form-group">
              <label htmlFor="var-name">Name <span className="required">*</span></label>
              <input
                id="var-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter VAR name"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="var-details">Details <span className="required">*</span></label>
              <textarea
                id="var-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Describe this value at risk"
                rows={4}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="wizard-step">
            <h4>Classification</h4>
            <div className="form-group">
              <label htmlFor="var-subcategory">Subcategory <span className="required">*</span></label>
              <select
                id="var-subcategory"
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value as VARSubcategory)}
                autoFocus
              >
                <option value="">-- Select subcategory --</option>
                {SUBCATEGORY_GROUPS.map((group) => (
                  <optgroup key={group.category} label={group.category}>
                    {group.subcategories.map((sub) => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
            {category && (
              <div className="form-group">
                <label>Category</label>
                <div className="derived-value">{category}</div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="wizard-step">
            <h4>Review &amp; Confirm</h4>
            <div className="review-summary">
              <div className="review-row">
                <span className="review-label">Location:</span>
                <span className="review-value">
                  {formatCoordinate(location.lat, true)}, {formatCoordinate(location.lon, false)}
                </span>
              </div>
              <div className="review-row">
                <span className="review-label">Region:</span>
                <span className="review-value">{locationValidation.regionName} ({locationValidation.regionCode})</span>
              </div>
              <div className="review-row">
                <span className="review-label">Name:</span>
                <span className="review-value">{name}</span>
              </div>
              <div className="review-row">
                <span className="review-label">Details:</span>
                <span className="review-value">{details}</span>
              </div>
              <div className="review-row">
                <span className="review-label">Category:</span>
                <span className="review-value">{category}</span>
              </div>
              <div className="review-row">
                <span className="review-label">Subcategory:</span>
                <span className="review-value">{subcategory}</span>
              </div>
              <div className="review-note">
                VAR ID will be auto-generated by the server. Photos can be added in the next step.
              </div>
            </div>
            {error && <div className="var-create-error">{error}</div>}
          </div>
        );

    }
  };

  return ReactDOM.createPortal(
    <div className="var-create-form-overlay" onClick={createdVarId ? onDone : onCancel} onKeyDown={handleKeyDown}>
      <div className="var-create-form" onClick={(e) => e.stopPropagation()}>
        <div className="var-create-form-header">
          <h3>{createdVarId ? `VAR ${createdVarId} Created` : 'Create New VAR'}</h3>
          <div className="step-indicator">Step {step} of {totalSteps}</div>
          <button className="close-btn" onClick={createdVarId ? onDone : onCancel} aria-label="Close" type="button">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="var-create-form-body">
          {renderStep()}
        </div>

        <div className="var-create-form-footer">
          {/* Back / Cancel */}
          {step > 1 ? (
            <button type="button" className="btn-secondary" onClick={handleBack} disabled={submitting}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back
            </button>
          ) : (
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={submitting}>
              Cancel
            </button>
          )}

          {/* Forward action */}
          {step < 4 && (
            <button type="button" className="btn-primary" onClick={handleNext} disabled={!canProceed}>
              Next <FontAwesomeIcon icon={faArrowRight} />
            </button>
          )}

          {step === 4 && !createdVarId && (
            <button type="button" className="btn-primary" onClick={handleCreateVAR} disabled={!canProceed || submitting}>
              <FontAwesomeIcon icon={faSave} />
              {submitting ? ' Creating...' : ' Create VAR'}
            </button>
          )}

          {createdVarId && !showPhotoUploader && (
            <button type="button" className="btn-primary" onClick={onDone}>
              Done
            </button>
          )}
        </div>
      </div>

      {createdVarId && (
        <MultiFileUploader
          contentType="var_photo"
          context={{ varId: createdVarId }}
          identifier={createdVarId}
          isOpen={showPhotoUploader}
          onClose={() => setShowPhotoUploader(false)}
          onUploadComplete={() => { /* refresh happens in Done handler via onDone */ }}
        />
      )}
    </div>,
    document.body
  );
};

export default VARCreateForm;
